import RAPIER, { type Collider, type RigidBody, type World } from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import { BonusCardAssetLibrary, type BonusCardObject } from '../../assets/cards';
import { CoinAssetLibrary, type CoinObject } from '../../assets/coins';
import type { MedalFixedStepHooks, MedalOutcome } from '../physics';

export interface PayoutTraySnapshot {
  readonly totalItems: number;
  readonly coins: number;
  readonly cards: number;
  readonly collectableItems: number;
  readonly fallingItems: number;
  readonly settledItems: number;
  readonly physicalItems: number;
  readonly overflowItems: number;
  readonly prizeCredits: number;
  readonly collections: number;
  readonly lastArrivalId: string | null;
  readonly lastCollectedItems: number;
  readonly lastCollectedCoins: number;
  readonly lastCollectedCards: number;
  readonly lastCollectedPrizeCredits: number;
}

export interface PayoutTrayCollection {
  readonly outcomes: readonly MedalOutcome[];
  readonly coins: number;
  readonly cards: number;
  readonly prizeCredits: number;
}

export interface PayoutTraySystemOptions {
  readonly world: World;
  readonly parent?: THREE.Object3D;
  readonly coinScale?: number;
  readonly cardScale?: number;
  readonly maxPhysicalItems?: number;
  readonly collectDelaySeconds?: number;
  readonly floorY?: number;
}

type TrayRecord = {
  readonly key: string;
  readonly outcome: MedalOutcome;
  readonly body: RigidBody | null;
  readonly collider: Collider | null;
  readonly visual: CoinObject | BonusCardObject | null;
  ageSeconds: number;
};

const DEFAULT_COIN_SCALE = 0.36;
const DEFAULT_CARD_SCALE = 0.24;
const DEFAULT_MAX_PHYSICAL_ITEMS = 48;
const DEFAULT_COLLECT_DELAY_SECONDS = 0.72;
const DEFAULT_FLOOR_Y = 1.21;
const TRAY_CENTER_Z = 1.82;

/**
 * A real Rapier-backed payout tray. Front wins are recreated below the chute,
 * fall into the visible basin, and stay claimable until the player collects.
 */
export class PayoutTraySystem {
  readonly root = new THREE.Group();
  readonly stepHooks: MedalFixedStepHooks;
  readonly hitTarget: THREE.Mesh<THREE.BoxGeometry, THREE.MeshBasicMaterial>;

  private readonly world: World;
  private readonly coinAssets = new CoinAssetLibrary(24);
  private readonly cardAssets = new BonusCardAssetLibrary();
  private readonly boundaryBody: RigidBody;
  private readonly boundaryColliders: Collider[] = [];
  private readonly records: TrayRecord[] = [];
  private readonly recordKeys = new Set<string>();
  private readonly coinScale: number;
  private readonly cardScale: number;
  private readonly maxPhysicalItems: number;
  private readonly collectDelaySeconds: number;
  private readonly floorY: number;
  private collections = 0;
  private lastArrivalId: string | null = null;
  private lastCollectedItems = 0;
  private lastCollectedCoins = 0;
  private lastCollectedCards = 0;
  private lastCollectedPrizeCredits = 0;
  private disposed = false;

  constructor(options: PayoutTraySystemOptions) {
    this.world = options.world;
    this.coinScale = readPositive(options.coinScale ?? DEFAULT_COIN_SCALE, 'coinScale');
    this.cardScale = readPositive(options.cardScale ?? DEFAULT_CARD_SCALE, 'cardScale');
    this.maxPhysicalItems = readPositiveInteger(
      options.maxPhysicalItems ?? DEFAULT_MAX_PHYSICAL_ITEMS,
      'maxPhysicalItems',
    );
    this.collectDelaySeconds = readNonNegative(
      options.collectDelaySeconds ?? DEFAULT_COLLECT_DELAY_SECONDS,
      'collectDelaySeconds',
    );
    this.floorY = readPositive(options.floorY ?? DEFAULT_FLOOR_Y, 'floorY');

    this.root.name = 'PayoutTraySystem';
    options.parent?.add(this.root);
    this.boundaryBody = this.createBoundaries();

    this.hitTarget = new THREE.Mesh(
      new THREE.BoxGeometry(2.08, 0.72, 0.82),
      new THREE.MeshBasicMaterial({
        color: '#ffffff',
        transparent: true,
        opacity: 0,
        depthWrite: false,
        colorWrite: false,
      }),
    );
    this.hitTarget.name = 'Payout tray collection hit target';
    this.hitTarget.position.set(0, this.floorY + 0.28, TRAY_CENTER_Z);
    this.hitTarget.renderOrder = -1;
    this.root.add(this.hitTarget);

    this.stepHooks = Object.freeze({
      afterStep: (fixedDeltaSeconds: number) => this.afterPhysicsStep(fixedDeltaSeconds),
    });
  }

  get snapshot(): PayoutTraySnapshot {
    let coins = 0;
    let cards = 0;
    let collectableItems = 0;
    let settledItems = 0;
    let physicalItems = 0;
    let prizeCredits = 0;
    for (const record of this.records) {
      if (record.outcome.itemKind === 'coin') coins += 1;
      else {
        cards += 1;
        prizeCredits += record.outcome.denomination;
      }
      if (record.ageSeconds >= this.collectDelaySeconds) collectableItems += 1;
      if (record.body) {
        physicalItems += 1;
        const position = record.body.translation();
        if (record.body.isSleeping() || position.y <= this.floorY + 0.2) settledItems += 1;
      }
    }
    return Object.freeze({
      totalItems: this.records.length,
      coins,
      cards,
      collectableItems,
      fallingItems: this.records.length - collectableItems,
      settledItems,
      physicalItems,
      overflowItems: this.records.length - physicalItems,
      prizeCredits,
      collections: this.collections,
      lastArrivalId: this.lastArrivalId,
      lastCollectedItems: this.lastCollectedItems,
      lastCollectedCoins: this.lastCollectedCoins,
      lastCollectedCards: this.lastCollectedCards,
      lastCollectedPrizeCredits: this.lastCollectedPrizeCredits,
    });
  }

  enqueue(outcome: MedalOutcome): PayoutTraySnapshot {
    this.assertActive();
    if (outcome.result !== 'front-win') {
      throw new Error('Only front-win outcomes can enter the payout tray.');
    }
    const key = `${outcome.itemKind}:${outcome.id}`;
    if (this.recordKeys.has(key)) return this.snapshot;

    const physical = this.records.filter((record) => record.body !== null).length < this.maxPhysicalItems;
    const record = physical
      ? this.createPhysicalRecord(key, outcome)
      : { key, outcome, body: null, collider: null, visual: null, ageSeconds: 0 };
    this.records.push(record);
    this.recordKeys.add(key);
    this.lastArrivalId = outcome.id;
    return this.snapshot;
  }

  collectReady(): PayoutTrayCollection {
    this.assertActive();
    const outcomes: MedalOutcome[] = [];
    for (let index = this.records.length - 1; index >= 0; index -= 1) {
      const record = this.records[index];
      if (record.ageSeconds < this.collectDelaySeconds) continue;
      this.records.splice(index, 1);
      this.recordKeys.delete(record.key);
      outcomes.push(record.outcome);
      this.disposeRecord(record);
    }
    outcomes.reverse();
    let coins = 0;
    let cards = 0;
    let prizeCredits = 0;
    for (const outcome of outcomes) {
      if (outcome.itemKind === 'coin') coins += 1;
      else {
        cards += 1;
        prizeCredits += outcome.denomination;
      }
    }
    if (outcomes.length > 0) {
      this.collections += 1;
      this.lastCollectedItems = outcomes.length;
      this.lastCollectedCoins = coins;
      this.lastCollectedCards = cards;
      this.lastCollectedPrizeCredits = prizeCredits;
    }
    return Object.freeze({
      outcomes: Object.freeze(outcomes),
      coins,
      cards,
      prizeCredits,
    });
  }

  clear(): void {
    this.assertActive();
    for (const record of this.records.splice(0)) this.disposeRecord(record);
    this.recordKeys.clear();
    this.lastArrivalId = null;
    this.lastCollectedItems = 0;
    this.lastCollectedCoins = 0;
    this.lastCollectedCards = 0;
    this.lastCollectedPrizeCredits = 0;
  }

  dispose(): void {
    if (this.disposed) return;
    this.clear();
    this.disposed = true;
    this.hitTarget.geometry.dispose();
    this.hitTarget.material.dispose();
    this.hitTarget.removeFromParent();
    if (this.world.bodies.contains(this.boundaryBody.handle)) {
      this.world.removeRigidBody(this.boundaryBody);
    }
    this.boundaryColliders.length = 0;
    this.coinAssets.dispose();
    this.cardAssets.dispose();
    this.root.removeFromParent();
    this.root.clear();
  }

  private createPhysicalRecord(key: string, outcome: MedalOutcome): TrayRecord {
    const seed = hashString(key);
    const random = createRandom(seed);
    const x = THREE.MathUtils.clamp(outcome.position.x * 0.52 + (random() - 0.5) * 0.18, -0.7, 0.7);
    const z = 1.66 + random() * 0.16;
    const y = this.floorY + 0.57 + random() * 0.1;

    if (outcome.itemKind === 'coin') {
      const visual = this.coinAssets.createCoin({
        variant: outcome.variant,
        finish: outcome.finish,
        seed: `tray:${key}`,
        name: `Payout coin ${outcome.id}`,
        castShadow: true,
        receiveShadow: true,
      });
      visual.scale.setScalar(this.coinScale);
      this.root.add(visual);
      const metadata = visual.metadata;
      const quaternion = new THREE.Quaternion().setFromEuler(new THREE.Euler(
        (random() - 0.5) * 0.18,
        random() * Math.PI * 2,
        (random() - 0.5) * 0.18,
      ));
      const body = this.world.createRigidBody(
        RAPIER.RigidBodyDesc.dynamic()
          .setTranslation(x, y, z)
          .setRotation(quaternion)
          .setLinvel((random() - 0.5) * 0.2, -0.35, 0.24 + random() * 0.12)
          .setAngvel({ x: 2.1 + random() * 1.4, y: (random() - 0.5) * 2.2, z: 1.4 })
          .setLinearDamping(0.42)
          .setAngularDamping(0.72)
          .setCanSleep(true)
          .setCcdEnabled(true)
          .setUserData({ kind: 'payout-coin', id: outcome.id }),
      );
      const collider = this.world.createCollider(
        RAPIER.ColliderDesc.cylinder(
          metadata.collision.halfHeight * this.coinScale,
          metadata.collision.radius * this.coinScale,
        )
          .setMass(metadata.physics.mass * this.coinScale ** 3)
          .setFriction(0.76)
          .setRestitution(0.06),
        body,
      );
      visual.syncFromPhysics(body);
      return { key, outcome, body, collider, visual, ageSeconds: 0 };
    }

    const visual = this.cardAssets.create({
      variantId: outcome.variantId,
      denomination: outcome.denomination,
      seed: `tray:${key}`,
      instanceId: `tray-${outcome.id}`,
      castShadow: true,
      receiveShadow: true,
    });
    visual.root.scale.setScalar(this.cardScale);
    visual.root.userData.payoutItemId = outcome.id;
    this.root.add(visual.root);
    const flat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI * 0.5);
    const yaw = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), (random() - 0.5) * 0.42);
    const quaternion = yaw.multiply(flat);
    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(x * 0.72, y + 0.08, z)
        .setRotation(quaternion)
        .setLinvel((random() - 0.5) * 0.1, -0.28, 0.15)
        .setAngvel({ x: 0.3, y: (random() - 0.5) * 0.55, z: 0.18 })
        .setLinearDamping(0.48)
        .setAngularDamping(0.66)
        .setCanSleep(true)
        .setCcdEnabled(true)
        .setUserData({ kind: 'payout-card', id: outcome.id, denomination: outcome.denomination }),
    );
    const [halfX, halfY, halfZ] = visual.metadata.collision.halfExtents;
    const collider = this.world.createCollider(
      RAPIER.ColliderDesc.cuboid(
        halfX * this.cardScale,
        halfY * this.cardScale,
        halfZ * this.cardScale,
      )
        .setDensity(0.36)
        .setFriction(0.58)
        .setRestitution(0.04),
      body,
    );
    syncCardVisual(visual, body);
    return { key, outcome, body, collider, visual, ageSeconds: 0 };
  }

  private afterPhysicsStep(fixedDeltaSeconds: number): void {
    if (this.disposed) return;
    for (let index = 0; index < this.records.length; index += 1) {
      const record = this.records[index];
      record.ageSeconds += fixedDeltaSeconds;
      if (!record.body || !record.visual) continue;
      this.recoverEscapedBody(record.body, index);
      if (record.outcome.itemKind === 'coin') {
        (record.visual as CoinObject).syncFromPhysics(record.body);
      } else {
        syncCardVisual(record.visual as BonusCardObject, record.body);
      }
    }
  }

  private recoverEscapedBody(body: RigidBody, index: number): void {
    const position = body.translation();
    if (
      position.y >= this.floorY - 0.42
      && Math.abs(position.x) <= 1.08
      && position.z >= 1.34
      && position.z <= 2.28
    ) return;
    const column = index % 6;
    const row = Math.floor(index / 6) % 4;
    body.setTranslation({
      x: -0.58 + column * 0.232,
      y: this.floorY + 0.22 + row * 0.035,
      z: 1.65 + row * 0.105,
    }, true);
    body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    body.setAngvel({ x: 0, y: 0, z: 0 }, true);
  }

  private createBoundaries(): RigidBody {
    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setUserData({ kind: 'payout-tray-boundaries' }),
    );
    this.addBoundary(body, RAPIER.ColliderDesc.cuboid(0.92, 0.045, 0.33)
      .setTranslation(0, this.floorY - 0.045, TRAY_CENTER_Z));
    this.addBoundary(body, RAPIER.ColliderDesc.cuboid(1.02, 0.26, 0.045)
      .setTranslation(0, this.floorY + 0.25, 1.51));
    this.addBoundary(body, RAPIER.ColliderDesc.cuboid(1.02, 0.18, 0.045)
      .setTranslation(0, this.floorY + 0.17, 2.14));
    for (const side of [-1, 1]) {
      this.addBoundary(body, RAPIER.ColliderDesc.cuboid(0.045, 0.25, 0.34)
        .setTranslation(side * 0.965, this.floorY + 0.24, TRAY_CENTER_Z));
    }
    return body;
  }

  private addBoundary(body: RigidBody, descriptor: RAPIER.ColliderDesc): void {
    this.boundaryColliders.push(this.world.createCollider(
      descriptor.setFriction(0.78).setRestitution(0.035),
      body,
    ));
  }

  private disposeRecord(record: TrayRecord): void {
    if (record.visual) {
      if (record.outcome.itemKind === 'coin') (record.visual as CoinObject).dispose();
      else (record.visual as BonusCardObject).dispose();
    }
    if (record.body && this.world.bodies.contains(record.body.handle)) {
      this.world.removeRigidBody(record.body);
    }
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('PayoutTraySystem has been disposed.');
  }
}

function syncCardVisual(card: BonusCardObject, body: RigidBody): void {
  const translation = body.translation();
  const rotation = body.rotation();
  card.root.position.set(translation.x, translation.y, translation.z);
  card.root.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
}

function hashString(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function createRandom(seed: number): () => number {
  let state = seed || 0x6d2b79f5;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function readPositive(value: number, label: string): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${label} must be a finite number greater than zero.`);
  }
  return value;
}

function readNonNegative(value: number, label: string): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${label} must be a finite non-negative number.`);
  }
  return value;
}

function readPositiveInteger(value: number, label: string): number {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new RangeError(`${label} must be a positive safe integer.`);
  }
  return value;
}
